// @story HIR-HR5
// The page a candidate opens from their offer message. No login: the link is
// signed. They read the offer and accept or decline it, once, before the
// reply-by date.
//
// The words on this page are candidate-facing and are listed in the PR under
// "Wording for approval".
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { CheckCircle, XCircle, Clock, AlertCircle, Loader2 } from 'lucide-react';
import { publicApi } from '../services/api';
import { formatWhen } from '../components/common/offer/offerDates';

function Shell({ school, children }) {
  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl border shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100">
          <img src={`${process.env.PUBLIC_URL}/assets/dps-crest.png`} alt="" className="h-10" />
          <p className="text-[11px] font-semibold tracking-widest text-gray-400 uppercase">{school || 'Delhi Public School'}</p>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function OfferResponse() {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null); // 'accept' | 'decline'
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    publicApi.get(`/public/offers/${encodeURIComponent(token)}`)
      .then((r) => setOffer(r.data.data))
      .catch((e) => setError(e?.response?.data?.message || 'We could not open this offer just now. Please try again later.'))
      .finally(() => setLoading(false));
  }, [token]);

  const answer = async (decision) => {
    setBusy(true);
    try {
      await publicApi.post(`/public/offers/${encodeURIComponent(token)}/respond`, { decision });
      setDone(decision);
    } catch (e) {
      setError(e?.response?.data?.message || 'We could not record your answer. Please try again.');
    } finally { setBusy(false); setConfirm(null); }
  };

  if (loading) return <Shell><div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div></Shell>;

  if (error) return (
    <Shell school={offer?.school}>
      <div className="text-center py-8">
        <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <p className="text-gray-700">{error}</p>
        <p className="text-sm text-gray-500 mt-2">If you think this is a mistake, please contact the school's HR team.</p>
      </div>
    </Shell>
  );

  const state = done ? (done === 'accept' ? 'accepted' : 'declined') : offer.state;

  if (state !== 'sent') {
    const look = {
      accepted: [CheckCircle, 'text-green-500', 'Thank you — you have accepted this offer.', 'The school\'s HR team will be in touch about your next steps.'],
      declined: [XCircle, 'text-gray-400', 'You have declined this offer.', 'Thank you for letting us know, and for the time you gave to our process.'],
      rejected: [XCircle, 'text-gray-400', 'This offer has been declined.', ''],
      lapsed: [Clock, 'text-amber-500', 'The reply-by date for this offer has passed.', 'If you still wish to discuss it, please contact the school\'s HR team.'],
    }[state] || [AlertCircle, 'text-gray-400', 'This offer is no longer open.', 'Please contact the school\'s HR team.'];
    const [Icon, tone, title, sub] = look;
    return (
      <Shell school={offer.school}>
        <div className="text-center py-8">
          <Icon className={`w-12 h-12 mx-auto mb-3 ${tone}`} />
          <h1 className="text-xl font-bold text-gray-900 mb-1">{title}</h1>
          {sub && <p className="text-sm text-gray-500">{sub}</p>}
        </div>
      </Shell>
    );
  }

  return (
    <Shell school={offer.school}>
      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Your offer</p>
      <h1 className="text-2xl font-bold text-gray-900 mb-3">{offer.position}</h1>
      <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5 text-sm">
        {offer.compensation && <div className="bg-gray-50 rounded-lg p-3"><dt className="text-xs text-gray-500">Compensation</dt><dd className="font-semibold text-gray-800">{offer.compensation}</dd></div>}
        {offer.startDate && <div className="bg-gray-50 rounded-lg p-3"><dt className="text-xs text-gray-500">Start date</dt><dd className="font-semibold text-gray-800">{offer.startDate}</dd></div>}
        {offer.deadline && <div className="bg-orange-50 rounded-lg p-3"><dt className="text-xs text-orange-700">Please reply by</dt><dd className="font-semibold text-gray-800">{formatWhen(offer.deadline)}</dd></div>}
      </dl>

      {offer.letter && (
        <div className="prose prose-sm max-w-none border border-gray-100 rounded-xl p-4 mb-6 max-h-[28rem] overflow-auto">
          <ReactMarkdown>{offer.letter}</ReactMarkdown>
        </div>
      )}

      {confirm ? (
        <div className="rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-800 mb-3">
            {confirm === 'accept'
              ? 'You are about to accept this offer. Are you sure?'
              : 'You are about to decline this offer. This cannot be undone here. Are you sure?'}
          </p>
          <div className="flex gap-2">
            <button disabled={busy} onClick={() => answer(confirm)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50 ${confirm === 'accept' ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-700 hover:bg-gray-800'}`}>
              {busy ? 'Saving…' : confirm === 'accept' ? 'Yes, I accept' : 'Yes, I decline'}
            </button>
            <button disabled={busy} onClick={() => setConfirm(null)} className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100">Go back</button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-2">
          <button onClick={() => setConfirm('accept')} className="flex-1 px-4 py-3 rounded-lg text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600">Accept the offer</button>
          <button onClick={() => setConfirm('decline')} className="flex-1 px-4 py-3 rounded-lg text-sm font-semibold text-gray-700 border border-gray-300 hover:bg-gray-50">Decline</button>
        </div>
      )}
      <p className="text-xs text-gray-400 mt-4">Questions about the offer? Please contact the school's HR team before you answer.</p>
    </Shell>
  );
}
